import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { HiHeart } from "react-icons/hi";
import CustomBtn from "../../buttons/CustomBtn";
import SecureIcon from "../../../../assets/images/icons/secure.svg";
import PathConstants from "../../../../routes/pathConstants";
import { Styles } from "./styles";

const PREDEFINED_AMOUNTS = [5, 10, 25, 50, 100, 250];

const Step1 = ({
  formik,
  onNext,
  isAr,
  isInDetailsPage = false,
  isLoggedIn = false,
  showMonthlyOption = false,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const heartIconRef = useRef(null);
  const [flyingHeart, setFlyingHeart] = useState({ active: false, x: 0, y: 0 });

  const amount = formik.values.amount ?? "";
  const donationType = formik.values.donationType ?? "one-time";
  const selectedAmount = (() => {
    const n = Number(amount);
    return PREDEFINED_AMOUNTS.includes(n) ? n : null;
  })();
  const customAmount = String(amount ?? "");
  const amountError = formik.submitCount > 0 ? formik.errors?.amount : undefined;

  const revalidateAmount = () => {
    setTimeout(() => formik.validateField("amount"), 0);
  };

  const handleAmountClick = (amt) => {
    formik.setFieldValue("amount", String(amt));
    formik.setFieldTouched("amount", true);
    revalidateAmount();
  };

  const handleCustomAmountChange = (e) => {
    const raw = e.target.value;
    const value = raw === "" ? "" : raw.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
    formik.setFieldValue("amount", value);
    revalidateAmount();
  };

  const handleMonthlyClick = () => {
    if (donationType === "monthly") return;
    const el = heartIconRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      setFlyingHeart({
        active: true,
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      });
    }
    formik.setFieldValue("donationType", "monthly");
  };

  return (
    <Styles $isAr={isAr} $isInDetailsPage={isInDetailsPage}>
      <form className="donation-form" onSubmit={(e) => { e.preventDefault(); onNext?.(); }}>
        {
          !isInDetailsPage && (
            <div className="main-title">
              <img src={SecureIcon} alt="Secure" />
              <span>{t("Secure Donation")}</span>
            </div>
          )}

        {showMonthlyOption && (
          <div className="donation-type-toggle">
            <button
              type="button"
              className={donationType === "one-time" ? "active" : ""}
              onClick={() => formik.setFieldValue("donationType", "one-time")}
            >
              {t("One Time")}
            </button>
            <button
              type="button"
              className={donationType === "monthly" ? "active" : ""}
              onClick={handleMonthlyClick}
            >
              {t("Monthly")}{" "}
              <span ref={heartIconRef} className="monthly-heart-icon">
                <HiHeart />
              </span>
            </button>
          </div>
        )}

        {showMonthlyOption && flyingHeart.active && (
          <div
            className="flying-heart"
            style={{
              "--fly-x": `${flyingHeart.x}px`,
              "--fly-y": `${flyingHeart.y}px`,
            }}
            onAnimationEnd={() => setFlyingHeart((f) => ({ ...f, active: false }))}
          >
            <HiHeart />
          </div>
        )}

        {showMonthlyOption && donationType === "monthly" && !isLoggedIn ? (
          <>
            <p className="monthly-login-message">{t("Please login first to donate monthly")}</p>
            <CustomBtn
              type="button"
              text={t("Login")}
              color="var(--main-color)"
              textColor="var(--white)"
              width="100%"
              withoutTranslate={false}
              onClick={() =>
                navigate(PathConstants.LOGIN, { state: { from: location } })
              }
            />
          </>
        ) : (
          <>
            <div className="amount-grid">
              {PREDEFINED_AMOUNTS.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  className={`amount-btn ${selectedAmount === amount ? "selected" : ""}`}
                  onClick={() => handleAmountClick(amount)}
                >
                  ${amount}
                </button>
              ))}
            </div>

            <div className="custom-amount-section">
              <span className="currency-symbol">$</span>
              <input
                name="amount"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                className="custom-amount-input"
                placeholder="55"
                value={customAmount}
                onChange={handleCustomAmountChange}
              />
            </div>

            {amountError && (
              <p className="amount-error-message" role="alert">
                {amountError}
              </p>
            )}

            <CustomBtn
              type="submit"
              text={donationType === "one-time" ? t("Donate") : t("Donate Monthly")}
              color="var(--main-color)"
              textColor="var(--white)"
              width="100%"
              withoutTranslate={true}
            />
          </>
        )}
      </form>
    </Styles>
  );
};

export default Step1;
